/**
 * All content for the workflow-latency study, ported and sanitized from the
 * internal report. Numbers are production measurements; service names are
 * functional ("the workflow orchestrator"), never real — see SanitizedNote.
 *
 * Race model: one five-step run. `work` is provider generation and is
 * IDENTICAL in both tracks — that is the whole argument. `overhead` is ours.
 * Sums are calibrated to the measured p50s: 286 + 54 = 340s before,
 * 286 + 14 = 300s after.
 */

export const RACE_STEPS = [
  'prompt',
  'scene',
  'frame',
  'animate',
  'assemble',
] as const;

const WORK_SECONDS = [88, 34, 41, 96, 27];
const OVERHEAD_BEFORE = [13, 11, 10, 11, 9];
const OVERHEAD_AFTER = [4, 3, 2, 3, 2];

export type RaceSegment = {
  kind: 'work' | 'overhead';
  step: (typeof RACE_STEPS)[number];
  seconds: number;
  /** Left edge / width as % of the shared axis (the before-track length). */
  startPercent: number;
  widthPercent: number;
};

function buildSegments(overhead: number[]): RaceSegment[] {
  const segments: RaceSegment[] = [];
  let acc = 0;

  RACE_STEPS.forEach((step, i) => {
    for (const [kind, seconds] of [
      ['overhead', overhead[i]],
      ['work', WORK_SECONDS[i]],
    ] as const) {
      segments.push({
        kind,
        step,
        seconds,
        startPercent: (acc / RACE_SCALE) * 100,
        widthPercent: (seconds / RACE_SCALE) * 100,
      });
      acc += seconds;
    }
  });

  return segments;
}

export const TOTAL_BEFORE = [...WORK_SECONDS, ...OVERHEAD_BEFORE].reduce(
  (a, b) => a + b,
  0
); // 340
export const TOTAL_AFTER = [...WORK_SECONDS, ...OVERHEAD_AFTER].reduce(
  (a, b) => a + b,
  0
); // 300
/** Both lanes share the before-track axis so lengths compare honestly. */
export const RACE_SCALE = TOTAL_BEFORE;

export const SEGMENTS_BEFORE = buildSegments(OVERHEAD_BEFORE);
export const SEGMENTS_AFTER = buildSegments(OVERHEAD_AFTER);

export type Leak = {
  id: string;
  name: string;
  win: string;
  analogy: string;
  paragraphs: string[];
  bars: {
    beforeLabel: string;
    beforeValue: string;
    beforePercent: number;
    afterLabel: string;
    afterValue: string;
    afterPercent: number;
  };
};

export const LEAKS: Leak[] = [
  {
    id: 'queue-floor',
    name: 'Five seconds wasted between every step',
    win: 'up to 5s × 5 steps',
    analogy:
      'A bus that waits five seconds at every stop, even when nobody is boarding.',
    paragraphs: [
      'Work moved between steps through a queue set to wait up to five seconds before handing anything over, even when a message was already sitting there ready. A five-step run paid that toll five times, for nothing.',
      'I set the wait to zero and split the one shared queue into three lanes: long provider jobs, short "it\'s done" notices, and status checks. A two-second notification can no longer sit behind a ninety-second video job.',
    ],
    bars: {
      beforeLabel: 'Before · forced wait',
      beforeValue: '5.0s',
      beforePercent: 100,
      afterLabel: 'After · forced wait',
      afterValue: '0.0s',
      afterPercent: 2,
    },
  },
  {
    id: 'public-hops',
    name: 'Messages took the long route',
    win: '3.5s → 1.6s per handoff',
    analogy: 'Two people in the same building, mailing each other letters.',
    paragraphs: [
      'All three services run side by side, but they spoke to each other over public internet addresses: out of the building, around the block, back in. Sometimes a message got lost out there. Worse, every "your video is ready" notice was relayed through a third service acting as middleman.',
      'They now talk over internal bindings: direct calls that never leave the network. The middleman was measured, tested on 10% of traffic first, then deleted once it had proven unused for three days.',
    ],
    bars: {
      beforeLabel: 'Before · avg handoff',
      beforeValue: '3.5s',
      beforePercent: 100,
      afterLabel: 'After · avg handoff',
      afterValue: '1.6s',
      afterPercent: 46,
    },
  },
  {
    id: 'repair-sweep',
    name: 'Finished results waited up to 14 minutes to be noticed',
    win: '14 min → 66s',
    analogy: 'Your video was finished. Nobody had told the app yet.',
    paragraphs: [
      'When a completion notice went missing, a cleanup job ran every five minutes and was slow to act on what it found. A finished video could sit invisible to its owner for seven to fourteen minutes.',
      'A reconciler now runs every sixty seconds and asks the engine directly: did anything finish that I do not know about? Seven real recoveries have happened in production since launch, averaging 66 seconds. Anything that fails outright lands in a ledger someone can actually inspect, instead of vanishing.',
    ],
    bars: {
      beforeLabel: 'Before · worst case',
      beforeValue: '14 min',
      beforePercent: 100,
      afterLabel: 'After · measured avg',
      afterValue: '66s',
      afterPercent: 8,
    },
  },
  {
    id: 'shared-doc',
    name: 'Five steps fought over one shared record',
    win: 'duplicate-charge risk removed',
    analogy:
      "Two people editing the same sheet of paper. Someone's writing gets erased.",
    paragraphs: [
      'Every step wrote its status into one shared document. Two steps finishing at the same moment could overwrite each other, and answering "which step does this message belong to?" meant scanning every run in flight.',
      'Each step now owns its own row and claims it atomically, meaning only one worker can ever pick a step up and charge for it, so the same work can never be paid for twice. 1,471 step rows across 600 runs are tracked individually. Duplicate charges since launch: zero.',
    ],
    bars: {
      beforeLabel: 'Before · duplicate risk',
      beforeValue: 'possible',
      beforePercent: 100,
      afterLabel: 'After · duplicate risk',
      afterValue: 'none',
      afterPercent: 4,
    },
  },
];

export type ResultRow = {
  metric: string;
  sub: string;
  before: string;
  after: string;
  change: string;
  /** Rows are visually grouped; a divider renders before each new group. */
  group: number;
  /**
   * Same-unit numeric pair (seconds). Present → the row draws animated
   * before/after bars scaled within itself; absent → it reads as a statement.
   * Display strings above stay the label source of truth ("up to 14 min").
   */
  beforeN?: number;
  afterN?: number;
};

export const RESULT_ROWS: ResultRow[] = [
  {
    metric: 'Typical run',
    sub: 'median · p50',
    before: '340s',
    after: '300s',
    change: '−40s · −11.8%',
    group: 0,
    beforeN: 340,
    afterN: 300,
  },
  {
    metric: 'Slowest 10% of runs',
    sub: 'p90, where the waiting had piled up',
    before: '655s',
    after: '568s',
    change: '−87s · −13.3%',
    group: 0,
    beforeN: 655,
    afterN: 568,
  },
  {
    metric: 'Average run',
    sub: 'mean of all completions',
    before: '372s',
    after: '337s',
    change: '−35s · −9.4%',
    group: 0,
    beforeN: 372,
    afterN: 337,
  },
  {
    metric: 'Wait before a step starts',
    sub: 'ready → dispatched, first attempt',
    before: '3.5s',
    after: '2.4s',
    change: '96% within 5s',
    group: 1,
    beforeN: 3.5,
    afterN: 2.4,
  },
  {
    metric: 'A lost result surfaces in',
    sub: '7 real recoveries in production',
    before: 'up to 14 min',
    after: '66s',
    change: '−87%',
    group: 1,
    beforeN: 840,
    afterN: 66,
  },
  {
    metric: 'Time to process a completion',
    sub: 'notice received → run advanced',
    before: 'via middleman',
    after: '~200ms',
    change: 'hop removed',
    group: 1,
  },
  {
    metric: 'Being charged twice for one step',
    sub: 'the risk atomic claiming removes',
    before: 'possible',
    after: 'prevented',
    change: '0 since launch',
    group: 2,
  },
  {
    metric: 'What users receive',
    sub: 'prompts, models, output quality',
    before: 'frozen',
    after: 'frozen',
    change: 'unchanged',
    group: 2,
  },
];

export type RolloutStage = {
  id: string;
  date: string;
  name: string;
  /** One line, always visible on the rail. */
  summary: string;
  meta: string;
  body: string;
  gate: string;
};

export const ROLLOUT_STAGES: RolloutStage[] = [
  {
    id: 'dark-deploy',
    date: '31 Jul',
    name: 'Dark deploy',
    summary:
      'All three services deployed with every new behaviour switched off.',
    meta: '3 services · 4 queues · 3 migrations',
    body: 'New queues created, database migrated, all three services deployed, with every new behaviour switched off. Production ran exactly as before. This is the step that makes the rest safe.',
    gate: '~1,850 tests green across services, dry-run deploys clean',
  },
  {
    id: 'queue-lanes',
    date: '31 Jul',
    name: 'Split queue lanes',
    summary: 'Three queue lanes, and the five-second delivery floor removed.',
    meta: '1 PR in the generation engine',
    body: 'Provider dispatch, completion notices, and status polls each get their own lane, and the five-second delivery floor is removed. A short message can no longer queue behind a ninety-second video job.',
    gate: '20-minute watch · 20/20 green · 0 dead letters',
  },
  {
    id: 'internal-dispatch',
    date: '1 Aug',
    name: 'Internal dispatch',
    summary: 'Orchestrator-to-engine calls move onto an internal binding.',
    meta: '1 PR in the workflow orchestrator',
    body: 'Orchestrator-to-engine calls move off the public internet and onto an internal binding. This is the change that halved step pickup time.',
    gate: 'first-attempt pickup 3.5s → 1.6s average, 100% within 5s',
  },
  {
    id: 'direct-completions',
    date: '1 Aug',
    name: 'Direct completions',
    summary:
      'Completion notices go straight to the orchestrator: 10% first, then 100%.',
    meta: '2 PRs in the generation engine',
    body: 'Completion notices go straight from engine to orchestrator instead of being relayed by the backend. Rolled out to 10% first, selected deterministically by generation id, then to 100% once the auth path was proven.',
    gate: '20/20 green at both steps · completion processing ~200ms',
  },
  {
    id: 'middleman-retired',
    date: '3 Aug',
    name: 'Middleman retired',
    summary: 'The relay switched off, watched, then deleted.',
    meta: '2 PRs in the API backend',
    body: 'With no traffic left on it for three days, the relay was switched off, watched, and then deleted along with its feature flag and six now-obsolete tests.',
    gate: 'rollout complete · every flag live · 0 pending dead letters',
  },
];

export type Service = {
  name: string;
  role: string;
  changes: string[];
  diffstat: string;
};

export const SERVICES: Service[] = [
  {
    name: 'the workflow orchestrator',
    role: 'Owns the run: which step is next, what it costs, when it is done.',
    changes: [
      'Per-step rows with atomic claiming, engine service binding, minute-reconciler',
      'Switch dispatch onto the internal binding',
      'Acknowledge late duplicate completions instead of retrying them into the dead-letter ledger',
    ],
    diffstat: '3 PRs · +1,243 / −233 across 22 files · 2 migrations',
  },
  {
    name: 'the generation engine',
    role: 'Routes each step to a provider and survives their failures.',
    changes: [
      'Three queue lanes, shared dead-letter ledger, per-message acknowledgement',
      'Turn the lanes on in production',
      'Direct completions at 10% with a deterministic canary, then to 100%',
    ],
    diffstat: '4 PRs · +1,772 / −163 across 35 files · 917 tests green',
  },
  {
    name: 'the API backend',
    role: 'The app users actually talk to. Was the middleman.',
    changes: [
      'Pin the completion payload to a shared contract fixture across all three services',
      'Stop relaying workflow completions',
      'Delete the relay code once it had proven unused',
    ],
    diffstat: '3 PRs · 790 tests green · relay removed after 3 stable days',
  },
];

export type ExcludedItem = { title: string; reason: string };

export const EXCLUDED: ExcludedItem[] = [
  {
    title: 'Reasoning-effort reduction',
    reason:
      'Would cut text latency materially. Also risks prompt quality. Not our call to make.',
  },
  {
    title: 'Model swaps',
    reason:
      'Different behaviour, different output. That is a migration with its own risk budget.',
  },
  {
    title: 'Prompt rewriting or merging steps',
    reason: 'Changes the creative construction users already rely on.',
  },
  {
    title: 'Output token caps',
    reason: 'Cheap latency win, real truncation risk.',
  },
  {
    title: 'Parallelising the visual chain',
    reason:
      "Later steps consume earlier steps' images. The dependency is real.",
  },
  {
    title: 'Prompt compression changes',
    reason: 'Fires on 0.18% of runs. Not worth the behavioural risk.',
  },
];
